import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-600 mb-4">该文章不存在或已被删除</p>
          <Link
            to="/category?tab=news"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white font-medium rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>返回新闻列表
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '新闻资讯', path: '/category?tab=news' },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '南宫集团',
      url: 'https://www.nangong-group.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.nangong-group.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',')}
        ogType="article"
        canonicalPath={`/article/${article.id}`}
        schema={articleSchema}
      />

      <div className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="py-8 md:py-12">
          <div className="site-container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-500">{article.date}</span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4 leading-snug">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-xs text-foreground-500 pb-6 border-b border-background-200">
                  <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                  <span><i className="ri-eye-line mr-1"></i>{article.readCount.toLocaleString()} 阅读</span>
                </div>
              </div>

              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-6 border-t border-background-200">
                <div className="flex flex-wrap gap-2 mb-6">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs font-medium text-foreground-600 bg-background-100 px-3 py-1.5 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <Link
                  to="/category?tab=news"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>返回新闻列表
                </Link>
              </div>
            </div>
          </div>
        </article>
      </div>
    </>
  );
}